import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Box,
  Avatar,
  Alert,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Switch,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { authApi } from '../services/api';

interface ProfileFormData {
  name: string;
  email: string;
  phone: string;
}

interface PasswordFormData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const queryClient = useQueryClient();
  
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Toggle language function
  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'ar' : 'en';
    changeLanguage(newLanguage);
  };

  // Profile form
  const { 
    control: profileControl, 
    handleSubmit: handleProfileSubmit, 
    reset: resetProfile,
    formState: { errors: profileErrors }
  } = useForm<ProfileFormData>({
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    }
  });

  // Password form
  const { 
    control: passwordControl, 
    handleSubmit: handlePasswordSubmit, 
    reset: resetPassword,
    formState: { errors: passwordErrors },
    watch
  } = useForm<PasswordFormData>();

  const newPassword = watch('new_password');

  // Profile update mutation
  const profileUpdateMutation = useMutation({
    mutationFn: (data: ProfileFormData) => authApi.updateProfile(data),
    onSuccess: () => {
      setSuccessMessage(t('profile_updated_successfully'));
      setErrorMessage('');
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (error: any) => {
      setErrorMessage(error.response?.data?.message || t('profile_update_failed'));
      setSuccessMessage('');
    },
  });

  // Password update mutation
  const passwordUpdateMutation = useMutation({
    mutationFn: (data: PasswordFormData) => authApi.updatePassword(data),
    onSuccess: () => {
      setSuccessMessage(t('password_updated_successfully'));
      setErrorMessage('');
      setShowPasswordForm(false);
      resetPassword();
    },
    onError: (error: any) => {
      console.error('Password update error:', error);
      let errorMsg = t('password_update_failed');
      
      if (error.response?.data?.errors) {
        // Handle specific validation errors
        const errors = error.response.data.errors;
        if (errors.current_password) {
          errorMsg = errors.current_password[0];
        } else if (errors.new_password) {
          errorMsg = errors.new_password[0];
        }
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      setErrorMessage(errorMsg);
      setSuccessMessage('');
    },
  });

  const handleProfileUpdate = (data: ProfileFormData) => {
    profileUpdateMutation.mutate(data);
  };

  const handlePasswordUpdate = (data: PasswordFormData) => {
    // Clear any previous messages
    setSuccessMessage('');
    setErrorMessage('');
    
    // Validate form data
    if (!data.current_password || !data.new_password || !data.confirm_password) {
      setErrorMessage(t('all_fields_required'));
      return;
    }
    
    if (data.new_password !== data.confirm_password) {
      setErrorMessage(t('passwords_do_not_match'));
      return;
    }
    
    if (data.new_password.length < 6) {
      setErrorMessage(t('password_min_length_6'));
      return;
    }
    
    passwordUpdateMutation.mutate(data);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    resetProfile({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    });
    setErrorMessage('');
  };

  const handleCancelPassword = () => {
    setShowPasswordForm(false);
    resetPassword();
    setErrorMessage('');
  };

  // Auto-hide success message after 3 seconds
  React.useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" height={400} />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{t('user_not_found')}</Alert>
      </Container>
    );
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleColor = (role?: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'property_manager': return 'primary';
      case 'staff': return 'secondary';
      default: return 'default';
    }
  };

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'admin': return t('administrator');
      case 'property_manager': return t('property_manager');
      case 'staff': return t('staff');
      default: return t('user');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('my_profile')}
      </Typography>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Information Card */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  {t('profile_information')}
                </Typography>
                {!isEditing && (
                  <Button
                    startIcon={<EditIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => setIsEditing(true)}
                  >
                    {t('edit')}
                  </Button>
                )}
              </Box>

              {isEditing ? (
                <Box component="form" onSubmit={handleProfileSubmit(handleProfileUpdate)}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        name="name"
                        control={profileControl}
                        rules={{ required: t('name_required') }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label={t('full_name')}
                            error={!!profileErrors.name}
                            helperText={profileErrors.name?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="email"
                        control={profileControl}
                        rules={{ 
                          required: t('email_required'),
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: t('invalid_email_format')
                          }
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label={t('email_address')}
                            type="email"
                            error={!!profileErrors.email}
                            helperText={profileErrors.email?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="phone"
                        control={profileControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label={t('phone_number')}
                            type="tel"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      disabled={profileUpdateMutation.isPending}
                    >
                      {profileUpdateMutation.isPending ? t('saving') : t('save')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelEdit}
                      disabled={profileUpdateMutation.isPending}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('full_name')}
                      secondary={user.name}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('email_address')}
                      secondary={user.email}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('phone_number')}
                      secondary={user.phone || t('not_provided')}
                    />
                  </ListItem>
                </List>
              )}
            </CardContent>
          </Card>

          {/* Password Change Card */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t('change_password')}
                </Typography>
                {!showPasswordForm && (
                  <Button
                    startIcon={<KeyIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => setShowPasswordForm(true)}
                  >
                    {t('change_password')}
                  </Button>
                )}
              </Box>

              {showPasswordForm ? (
                <Box component="form" onSubmit={handlePasswordSubmit(handlePasswordUpdate)}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        name="current_password"
                        control={passwordControl}
                        rules={{ required: t('current_password_required') }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label={t('current_password')}
                            type="password"
                            error={!!passwordErrors.current_password}
                            helperText={passwordErrors.current_password?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="new_password"
                        control={passwordControl}
                        rules={{ 
                          required: t('new_password_required'),
                          minLength: {
                            value: 6,
                            message: t('password_min_length')
                          }
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label={t('new_password')}
                            type="password"
                            error={!!passwordErrors.new_password}
                            helperText={passwordErrors.new_password?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="confirm_password"
                        control={passwordControl}
                        rules={{ 
                          required: t('confirm_password_required'),
                          validate: value => value === newPassword || t('passwords_do_not_match')
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label={t('confirm_new_password')}
                            type="password"
                            error={!!passwordErrors.confirm_password}
                            helperText={passwordErrors.confirm_password?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      disabled={passwordUpdateMutation.isPending}
                    >
                      {passwordUpdateMutation.isPending ? t('updating') : t('update_password')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelPassword}
                      disabled={passwordUpdateMutation.isPending}
                    >
                      {t('cancel')}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('password_change_description')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Profile Summary & Settings */}
        <Grid item xs={12} md={4}>
          {/* Profile Summary Card */}
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{ 
                  width: 80, 
                  height: 80, 
                  mx: 'auto', 
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: '2rem'
                }}
              >
                {getInitials(user.name)}
              </Avatar>
              <Typography variant="h6" gutterBottom>
                {user.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {user.email}
              </Typography>
              <Chip
                label={getRoleLabel(user.role)}
                color={getRoleColor(user.role)}
                icon={user.is_admin ? <AdminIcon /> : <BusinessIcon />}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>

          {/* Account Info Card */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('account_information')}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={t('user_id')}
                    secondary={user.id}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t('account_status')}
                  />
                  <Box>
                    <Chip
                      label={user.is_active ? t('active') : t('inactive')}
                      color={user.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t('member_since')}
                    secondary={new Date(user.created_at).toLocaleDateString()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={t('email_verified')}
                  />
                  <Box>
                    <Chip
                      label={user.email_verified_at ? t('verified') : t('not_verified')}
                      color={user.email_verified_at ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Language Settings Card */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('preferences')}
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <LanguageIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('language')}
                    secondary={language === 'en' ? 'English' : 'العربية'}
                  />
                  <Switch
                    checked={language === 'ar'}
                    onChange={toggleLanguage}
                    color="primary"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Property Assignments (if any) */}
          {user.assigned_properties && user.assigned_properties.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('assigned_properties')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('you_have_access_to')} {user.assigned_properties.length} {t('properties').toLowerCase()}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProfilePage;
